import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { NavigationRequest } from '@mtm/conversation-ui-angular';
import { OptionVersion } from '../interfaces';

export interface MtmNavigationRequest extends NavigationRequest {
  targetId?: string;
  /**
   * Uuid of an unsent DRAFT to resume into the composer instead of focusing a message.
   *
   * A draft is deliberately absent from the rendered feed, so the message selectors
   * `messageId` drives can never match one. Set it (to the same uuid `messageId`
   * carries, which the base request requires) when the target is a draft; the feed's
   * exclusivity rules then drop the focus and thread halves.
   */
  draftMessageId?: string;
}

interface PlatformCommentsChatContext {
  entityType: string | null;
  pageId: string | null;
  entityName: string | null;
  participants: { email: string }[];
  projectId?: string | null;
  /**
   * Drive DIRECTORY uuid to anchor the drive conversation feed to (the org
   * entity's own id — brand/workspace/campaign directories are uuid-pinned).
   * Non-null switches the right panel from the legacy `<conversation-interface>`
   * chat to `<mtm-drive-comments-panel>`.
   */
  driveAnchorUuid?: string | null;
}
@Injectable({ providedIn: 'root' })
export class PlatformCommentsChatStateService {
  private static readonly DEFAULT_CONTEXT: PlatformCommentsChatContext = {
    entityType: null,
    pageId: null,
    entityName: null,
    participants: [],
    projectId: null,
    driveAnchorUuid: null,
  };

  private readonly subject: BehaviorSubject<PlatformCommentsChatContext> =
    new BehaviorSubject<PlatformCommentsChatContext>({ ...PlatformCommentsChatStateService.DEFAULT_CONTEXT });

  private readonly versionDataSubject = new Subject<OptionVersion>();

  private readonly navigationRequestSubject = new Subject<MtmNavigationRequest>();
  private pendingNavigationRequest: MtmNavigationRequest | null = null;

  private resetTimeoutId: number | null = null;

  readonly context$: ReturnType<BehaviorSubject<PlatformCommentsChatContext>['asObservable']> =
    this.subject.asObservable();

  readonly versionData$ = this.versionDataSubject.asObservable();

  readonly navigationRequest$ = this.navigationRequestSubject.asObservable();

  public getCurrentValue(): PlatformCommentsChatContext {
    return this.subject.getValue();
  }

  public setContext(update: Partial<PlatformCommentsChatContext> | null): void {
    this.clearPendingReset();
    if (!update) {
      this.resetContext();
      return;
    }
    const current: PlatformCommentsChatContext = this.subject.getValue();
    const pageId = update.pageId ?? current.pageId;
    const isSamePage = pageId === current.pageId;
    this.subject.next({
      entityType: update.entityType ?? current.entityType,
      pageId,
      entityName: update.entityName ?? (isSamePage ? current.entityName : null),
      participants: update.participants ?? (isSamePage ? current.participants : []),
      projectId: update.projectId ?? undefined,
      driveAnchorUuid: update.driveAnchorUuid ?? (isSamePage ? current.driveAnchorUuid : null),
    });
  }

  public requestNavigation(request: MtmNavigationRequest): void {
    this.pendingNavigationRequest = { ...request };
    this.navigationRequestSubject.next(this.pendingNavigationRequest);
  }

  public getPendingNavigationRequest(): MtmNavigationRequest | null {
    return this.pendingNavigationRequest;
  }

  public consumePendingNavigationRequest(): MtmNavigationRequest | null {
    const request = this.pendingNavigationRequest;
    this.pendingNavigationRequest = null;
    return request;
  }

  public resetContext(): void {
    this.subject.next({ ...PlatformCommentsChatStateService.DEFAULT_CONTEXT });
  }

  public setVersionData(versionData: OptionVersion): void {
    this.versionDataSubject.next(versionData);
  }

  public scheduleReset(delayMs: number = 300): void {
    this.clearPendingReset();
    this.resetTimeoutId = setTimeout(() => {
      this.resetTimeoutId = null;
      this.resetContext();
    }, delayMs) as unknown as number;
  }

  private clearPendingReset(): void {
    if (this.resetTimeoutId != null) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
  }
}
